import { useEffect, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import PropTypes from "prop-types";
import { Typography, FormControl, Select } from "@material-ui/core/";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownSharpIcon from "@material-ui/icons/KeyboardArrowDownSharp";

const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%",
    border: "1px solid " + theme.palette.border.main,
    borderRadius: theme.borderRadius.default,
    height: "auto",
  },
  select: {
    border: "0px",
    backgroundColor: theme.palette.background.white,
    height: "2rem",
    background: theme.palette.background.white,
    paddingLeft: theme.spacing(1),
    "& > option": {
      fontSize: "3rem",
    },
    "& > select:focus": {
      backgroundColor: theme.palette.background.white,
    },
  },
  customIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    pointerEvents: "none",
    width: "2rem",
    height: "100%",
    color: theme.palette.background.white,
    backgroundColor: theme.palette.button.main,
    "& > svg": {
      width: "1rem",
    },
  },
}));

const DropdownField = ({ name, label, choices = [], onChange }) => {
  const classes = useStyles();
  const { register, unregister, watch, setValue } = useFormContext();
  const currentValue = watch(name);

  useEffect(() => {
    register(name);

    return () => {
      unregister(name, { keepValue: true });
    };
  }, [name, register, unregister]);

  // without a guide option, this set the first option's value of it
  useEffect(() => {
    const [choice] = choices;
    if (choice && currentValue == null) {
      const { key } = choice;
      setValue(name, key);
      onChange(name, key);
    }
  }, [name, choices, currentValue, setValue, onChange]);

  const handleChange = useCallback(
    (event) => {
      const newValue = event.target.value;

      setValue(name, newValue);
      onChange(name, newValue);
    },
    [name, setValue, onChange]
  );

  const CustomIcon = () => {
    return (
      <svg className={classes.customIcon}>
        <KeyboardArrowDownSharpIcon />
      </svg>
    );
  };

  return (
    <>
      <Typography variant="body1">{label}</Typography>
      <FormControl className={classes.form}>
        <Select
          native
          value={currentValue}
          onChange={handleChange}
          label={name}
          disableUnderline
          inputProps={{
            name: name,
          }}
          className={classes.select}
          IconComponent={() => <CustomIcon />}
        >
          {choices.map(({ key, label }) => {
            return (
              <option value={key} key={key} className={classes.label}>
                {label}
              </option>
            );
          })}
        </Select>
      </FormControl>
    </>
  );
};

DropdownField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  choices: PropTypes.array.isRequired,
  validations: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default DropdownField;
